/**
 * 効果音再生の単一エントリポイント。
 *
 * 設計:
 * - シングルトン `AudioContext` を lazy init。 iOS Safari の autoplay 制限のため
 *   `unlock()` を最初の user gesture (= pointerdown) で呼ぶ。
 * - 各音ファイルは初回再生時に fetch + decodeAudioData → ArrayBuffer をメモリキャッシュ。
 *   2 回目以降は cache hit で即再生 (= 着手レイテンシ < 5ms)。
 * - 各 kind に **単一音** (= ランダムローテーション無し、 ユーザー指示 2026-06-16)。
 * - `usePrefs().soundEnabled === false` のときは早期 return (= no-op)。
 * - 音量は固定 0.5 (v1)。 後で settings から弄れるようにする予定。
 *
 * 音源: Kenney (CC0)。 attribution 任意、 商用可。 `public/sounds/COPYING.md` に明記予定。
 */

import { usePrefs } from '../state/prefs';

export type SoundKind =
  | 'wood'    // 木駒 (将棋・チェス系)
  | 'stone'   // 石 (碁石・リバーシ石系)
  | 'card'    // カード配り・公開 (Poker の deal / NextHand / Provide*)
  | 'chip'    // チップ (Poker の Bet / Raise / Call / Check / AllIn)
  | 'fold'    // フォールド (Poker のカード捨て)
  | 'dice'    // サイコロ (Backgammon の振り直し)
  | 'capture'; // 取り音 (駒を取った時、 wood の重め)

const SOUND_URL: Record<SoundKind, string> = {
  wood: '/sounds/wood.ogg',
  stone: '/sounds/stone.ogg',
  card: '/sounds/card.ogg',
  chip: '/sounds/chip.ogg',
  fold: '/sounds/fold.ogg',
  dice: '/sounds/dice.ogg',
  capture: '/sounds/capture.ogg',
};

const DEFAULT_VOLUME = 0.5;

let ctx: AudioContext | null = null;
const buffers = new Map<string, AudioBuffer | Promise<AudioBuffer>>();

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

async function loadBuffer(url: string): Promise<AudioBuffer> {
  const c = getCtx();
  if (!c) throw new Error('no audio context');
  const cached = buffers.get(url);
  if (cached) return cached;
  const p = (async () => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`sound fetch ${url}: ${res.status}`);
    const ab = await res.arrayBuffer();
    return await c.decodeAudioData(ab);
  })();
  buffers.set(url, p);
  try {
    const buf = await p;
    buffers.set(url, buf);
    return buf;
  } catch (e) {
    buffers.delete(url); // 失敗したら次回再試行
    throw e;
  }
}

/**
 * AudioContext を user gesture で「起こす」。 iOS Safari は user gesture 後でないと
 * `resume()` しても音が出ない。 Play.tsx mount 時の最初の pointerdown で呼ぶ。
 */
export async function unlockAudio(): Promise<void> {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    try {
      await c.resume();
    } catch {
      /* user gesture が無いと失敗するが silent */
    }
  }
  // ついでに wood だけ事前 fetch して warm cache (= 初手のレイテンシゼロ化)。
  void loadBuffer(SOUND_URL.wood).catch(() => {});
}

/**
 * `kind` の音を再生。 失敗時 silent。
 * `usePrefs().soundEnabled === false` なら no-op。
 */
export function playSound(kind: SoundKind): void {
  if (!usePrefs.getState().soundEnabled) return;
  const c = getCtx();
  if (!c) return;
  const url = SOUND_URL[kind];
  void (async () => {
    try {
      const buf = await loadBuffer(url);
      if (c.state === 'suspended') {
        try {
          await c.resume();
        } catch {
          return;
        }
      }
      const src = c.createBufferSource();
      src.buffer = buf;
      const gain = c.createGain();
      gain.gain.value = DEFAULT_VOLUME;
      src.connect(gain).connect(c.destination);
      src.start();
    } catch {
      /* silent */
    }
  })();
}
